import { DOCUMENT } from '@angular/common';
import { Injectable, effect, inject, signal } from '@angular/core';
import { THEME } from '@/core/constants/local-storage-key';
import { LocalStorageService } from '../local-storage/local-storage.service';

export type Theme = 'light' | 'dark';

/**
 * Thème clair / sombre de l'application.
 *
 * Le thème sombre repose sur la classe `dark` posée sur `<html>` : `styles.css`
 * redéfinit les variables de couleur sous `.dark` et déclare la variante Tailwind
 * `dark:` sur ses descendants. Le choix est mémorisé dans le localStorage ; à défaut,
 * on suit la préférence du système. Un script dans `index.html` pose la classe avant
 * le démarrage d'Angular pour éviter un flash de thème clair au chargement.
 *
 * Le thème par défaut est **clair**, quel que soit le réglage du système : tant que les
 * couleurs figées en clair ne sont pas migrées (cf. `docs/DARK_MODE_A_CORRIGER.md`), le
 * mode sombre reste un choix explicite de l'utilisateur. Pour qu'il suive de nouveau le
 * système, remplacer le retour `'light'` de `themeInitial()` par `matchMedia` (et l'équivalent
 * dans le script d'`index.html`).
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly storage = inject(LocalStorageService);

  readonly theme = signal<Theme>(this.themeInitial());

  constructor() {
    effect(() => {
      const theme = this.theme();
      this.document.documentElement.classList.toggle('dark', theme === 'dark');
      this.document.documentElement.style.colorScheme = theme;
      this.storage.setState(THEME, theme);
    });
  }

  basculer(): void {
    this.theme.update((t) => (t === 'dark' ? 'light' : 'dark'));
  }

  private themeInitial(): Theme {
    const enregistre = this.storage.getState<Theme>(THEME);
    return enregistre === 'dark' ? 'dark' : 'light';
  }
}
